import MyContext from '@/shared/contexts/AppContext';
import { AttendanceSheetStatusEnum, getAttendanceSheetStatusEnumOptions } from '../../../enums/attendance-sheet-status.enum';
import { CarregarPermissoes, ValidarPermissao } from '@/modules/system/auth/application/auth.service';
import { IOpcao } from '@/shared/types/selectOption';
import { ListAllMonthsOfAnIntershipResponseDto, ReadEstagiarioSelectDto, ResponseEstagioDto, AttendanceSheetResponseDto } from '@/api.types';
import { PermissionEnum } from '@/modules/system/auth/domain/enums/permission.enum';
import { RoleEnum } from '@/modules/system/auth/domain/enums/role.enum';
import { listAllMonthsOfAnIntership, createAttendanceSheet } from '../../../attendance-sheet.service';
import { listarEstagios } from '@/modules/people/estagio.service';
import { readEstagiarioForAttendanceSheetSelect } from '@/modules/people/estagiario.service';
import { toast } from 'react-toastify';
import { useParams, useSearchParams } from 'react-router-dom';
import { useState, useEffect, useMemo, useContext } from 'react';

export function usePerIntern() {
  const context = useContext(MyContext);
  if (!context)
    throw new Error('Contexto deve ser usado dentro de um Provedor.');

  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const permissoes = useMemo(() => CarregarPermissoes(), []);
  const permissaoCriarFolhas = ValidarPermissao(permissoes, [{ role: RoleEnum.FOLHA, level: PermissionEnum.CREATE }]);

  const [loaded, setLoaded] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);

  const [registros, setRegistros] = useState<ListAllMonthsOfAnIntershipResponseDto[]>([]);
  const [filtrosStatus, setFiltrosStatus] = useState<IOpcao[]>([]);
  const [estagiarios, setEstagiarios] = useState<ReadEstagiarioSelectDto[]>([]);
  const [estagios, setEstagios] = useState<ResponseEstagioDto[]>([]);

  const [estagiarioId, setEstagiarioId] = useState<number>();
  const [estagio, setEstagio] = useState<ResponseEstagioDto>();
  const [statusBusca, setStatusBusca] = useState<AttendanceSheetStatusEnum | number>(0);

  const [selectedItem, setSelectedItem] = useState<ListAllMonthsOfAnIntershipResponseDto>();
  const [folhaSelecionada, setFolhaSelecionada] = useState<ListAllMonthsOfAnIntershipResponseDto>();

  const [showModalCriarFolha, setShowModalCriarFolha] = useState(false);
  const [showModalNotificacaoIndividual, setShowModalNotificacaoIndividual] = useState(false);
  const [showModalResponsavel, setShowModalResponsavel] = useState(false);
  const [showModalAssinaturas, setShowModalAssinaturas] = useState(false);
  const [folhaParaAssinaturas, setFolhaParaAssinaturas] = useState<AttendanceSheetResponseDto | null>(null);

  // Carregar estagiários e inicializar estagiário selecionado
  useEffect(() => {
    const carregarEstagiarios = async () => {
      try {
        const estagiariosRes = await readEstagiarioForAttendanceSheetSelect();
        setEstagiarios(estagiariosRes[0]);

        if (estagiariosRes[0].length === 0) {
          setLoaded(true);
          return;
        }

        // Verificar se há estagiarioId nos params ou searchParams
        const estagiarioIdParam = params.estagiarioId
          ? Number(params.estagiarioId)
          : searchParams.get('estagiarioId')
            ? Number(searchParams.get('estagiarioId'))
            : undefined;

        if (estagiarioIdParam && estagiariosRes[0].some(e => e.id === estagiarioIdParam)) {
          setEstagiarioId(estagiarioIdParam);
        } else if (!estagiarioId) {
          setEstagiarioId(estagiariosRes[0][0].id);
        }
      } catch (error) {
        toast.error(String(error));
        setLoaded(true);
      }
    };

    carregarEstagiarios();
  }, []);

  // Ler parâmetros iniciais dos searchParams
  useEffect(() => {
    const params = Object.fromEntries(searchParams.entries());
    if (params.status && !statusBusca) {
      setStatusBusca(parseInt(params.status));
    }
  }, [searchParams]);

  // Atualizar searchParams quando estagiarioId mudar
  useEffect(() => {
    if (estagiarioId) {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('estagiarioId', estagiarioId.toString());
        if (estagio) {
          newParams.set('estagioId', estagio.id.toString());
        } else {
          newParams.delete('estagioId');
        }
        statusBusca !== 0 ? newParams.set('status', statusBusca.toString()) : newParams.delete('status');
        return newParams;
      });
    }
  }, [estagiarioId, estagio, statusBusca, setSearchParams]);

  // Carregar estágios quando estagiário mudar
  useEffect(() => {
    if (!estagiarioId) {
      setEstagios([]);
      setEstagio(undefined);
      setRegistros([]);
      if (!loaded) setLoaded(true);
      return;
    }

    const carregarEstagios = async () => {
      try {
        const { objects } = await listarEstagios(estagiarioId);
        setEstagios(objects);

        // Verificar se há estagioId nos searchParams
        const estagioIdParam = searchParams.get('estagioId');
        if (estagioIdParam && objects.some(e => e.id === Number(estagioIdParam))) {
          setEstagio(objects.find(e => e.id === Number(estagioIdParam)));
        } else if (objects.length > 0) {
          setEstagio(objects[0]);
        } else {
          setEstagio(undefined);
          setRegistros([]);
          if (!loaded) setLoaded(true);
        }
      } catch (error) {
        toast.error('Falha ao carregar estágios.');
        setEstagios([]);
        setEstagio(undefined);
        if (!loaded) setLoaded(true);
      }
    };

    carregarEstagios();
  }, [estagiarioId]);

  // Buscar folhas quando estágio mudar
  const handleBuscar = () => {
    if (!estagio) {
      setRegistros([]);
      setFiltrosStatus([]);
      return;
    }

    listAllMonthsOfAnIntership(estagio.id)
      .then((res) => {
        let registrosFiltrados = res;

        // Filtrar por status
        if (statusBusca > 0) {
          registrosFiltrados = registrosFiltrados.filter(r => r.status === statusBusca);
        } else if (statusBusca === -1) {
          registrosFiltrados = registrosFiltrados.filter(r => r.status !== AttendanceSheetStatusEnum.CONCLUIDA);
        }

        setRegistros(registrosFiltrados);

        // Atualizar filtros de status com contadores
        const tStatus = getAttendanceSheetStatusEnumOptions().map(item => {
          const qtd = res.filter(a => a.status === item.value).length;
          return { value: Number(item.value), label: `${item.label} ${qtd > 0 ? `(${qtd})` : ''}` };
        });

        const qtdExcetoConcluidos = res.filter(a => a.status !== AttendanceSheetStatusEnum.CONCLUIDA).length;
        tStatus.unshift({ value: -1, label: `Exceto concluídos ${qtdExcetoConcluidos > 0 ? `(${qtdExcetoConcluidos})` : ''}` });
        setFiltrosStatus(tStatus);
      })
      .catch((error) => {
        toast.error(String(error));
        setRegistros([]);
      })
      .finally(() => setLoaded(true));
  };

  useEffect(() => {
    if (estagio) {
      handleBuscar();
    } else if (loaded && !estagio && estagiarioId) {
      // Se já carregou mas não tem estágio, garantir que loaded está true
      setRegistros([]);
    }
  }, [estagio, statusBusca]);

  const handleCriarFolha = () => {
    if (!selectedItem || !estagio) return;
    setIsExecuting(true);
    createAttendanceSheet(estagio.id, selectedItem.ano, selectedItem.mes)
      .then(res => {
        toast.success(res ? 'Folha Criada' : String(res));
        handleBuscar();
      })
      .catch(res => toast.error(String(res)))
      .finally(() => setIsExecuting(false));
  };

  const estagiarioNome = estagiarios.find(e => e.id === estagiarioId)?.pessoa?.nome;

  return {
    estagiarioId, setEstagiarioId,
    estagio, setEstagio,
    estagiarios, estagios,
    estagiarioNome,
    statusBusca, setStatusBusca,
    filtrosStatus, registros, handleBuscar,
    permissaoCriarFolhas,
    isExecuting, handleCriarFolha,
    selectedItem, setSelectedItem,
    folhaSelecionada, setFolhaSelecionada,
    showModalCriarFolha, setShowModalCriarFolha,
    showModalNotificacaoIndividual, setShowModalNotificacaoIndividual,
    showModalResponsavel, setShowModalResponsavel,
    showModalAssinaturas, setShowModalAssinaturas,
    folhaParaAssinaturas, setFolhaParaAssinaturas,
    loaded,
  };
}

